/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import {
  selectVisualFilters,
  selectHotelName,
  selectPmsSync,
  selectTrendBookingDate,
  selectTrendTargetDate,
  selectEnableHotelGroups,
  selectTrendOtb,
  selectLocalCurrency,
  selectBreakdownDetails,
} from 'modules/dashboard/selectors';
import { getSelectedBreakdown } from 'modules/dashboard/functions';
import {
  selectIsCumulative,
  selectTrendTimeWeeklyView,
  selectTrendSegmentFocusOn,
  selectTrendHotelNormalizedView,
  selectEnableSecondaryHotelGroups,
  selectTrendSegmentChartBreakdown,
  selectIsSegmentDetailed,
  selectIsForecast,
  selectSegmentGroup1,
  selectSegmentGroup2,
  selectTrendTimeCustomDate,
  selectTrendTimeEndDate,
  selectTrendTimeStartDate,
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeDateComparison,
  selectTabularView,
  selectTrendTimeAlignmentToggle,
  selectActiveTrendTab,
  selectRevenueIsDisplayTT,
  selectOccupiedRoomsIsDisplayTT,
  selectAdrIsDisplayTT,
  selectAvgBookingWindowIsDisplayTT,
  selectAvgLosIsDisplayTT,
  selectCxlIsDisplayTT,
  selectRevenueIsDisplayTS,
  selectOccupiedRoomsIsDisplayTS,
  selectAdrIsDisplayTS,
  selectAvgBookingWindowIsDisplayTS,
  selectCxlIsDisplayTS,
  selectAvgLosIsDisplayTS,
  selectRevenueIsDisplayTH,
  selectOccupiedRoomsIsDisplayTH,
  selectAdrIsDisplayTH,
  selectAvgBookingWindowIsDisplayTH,
  selectAvgLosIsDisplayTH,
  selectCxlIsDisplayTH,
  selectTrendHotelFocusOn,
  selectSymmetricComparisonIsDisplay,
  selectIsPrimary,
  selectIsPercentageChange,
  selectIsTrendDeAggregate,
  selectIsMovingAverage,
  selectMovingAverage,
  selectIsTsRevenueRibbonView,
  selectIsTsOccupiedRoomsRibbonView,
  selectIsTsAdrRibbonView,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import { useEffect, useMemo } from 'react';
import { generateTrendTimeShareUrl, getFocusOnFilter } from '../../inner-filter/functions';

const useGenerateTrendUrl = () => {
  const dispatch = useDispatch();

  const bookingDate = useSelector(selectTrendBookingDate);
  const targetDate = useSelector(selectTrendTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const tabularView = useSelector(selectTabularView);
  // time
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const isCumulative = useSelector(selectIsCumulative);
  const isTrendForecast = useSelector(selectIsForecast);
  //
  const vOTB = useSelector(selectTrendOtb);
  //
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const segmentGroup1 = useSelector(selectSegmentGroup1);
  const segmentGroup2 = useSelector(selectSegmentGroup2);
  //
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const normalizedViewToggle = useSelector(selectTrendHotelNormalizedView);
  const compareWindowCollapseToggle = useSelector(selectSymmetricComparisonIsDisplay);
  //
  const alignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const startDateComparison = useSelector(selectTrendTimeStartDate);
  const endDateComparison = useSelector(selectTrendTimeEndDate);
  const customDate = useSelector(selectTrendTimeCustomDate);
  //
  const activeTab = useSelector(selectActiveTrendTab);
  //
  const revenueIsDisplayTT = useSelector(selectRevenueIsDisplayTT);
  const occupiedRoomsIsDisplayTT = useSelector(selectOccupiedRoomsIsDisplayTT);
  const adrIsDisplayTT = useSelector(selectAdrIsDisplayTT);
  const avgBookingWindowIsDisplayTT = useSelector(selectAvgBookingWindowIsDisplayTT);
  const avgLosIsDisplayTT = useSelector(selectAvgLosIsDisplayTT);
  const cxlIsDisplayTT = useSelector(selectCxlIsDisplayTT);
  //
  const revenueIsDisplayTS = useSelector(selectRevenueIsDisplayTS);
  const occupiedRoomsIsDisplayTS = useSelector(selectOccupiedRoomsIsDisplayTS);
  const adrIsDisplayTS = useSelector(selectAdrIsDisplayTS);
  const avgBookingWindowIsDisplayTS = useSelector(selectAvgBookingWindowIsDisplayTS);
  const avgLosIsDisplayTS = useSelector(selectAvgLosIsDisplayTS);
  const cxlIsDisplayTS = useSelector(selectCxlIsDisplayTS);
  //
  const revenueIsDisplayTH = useSelector(selectRevenueIsDisplayTH);
  const occupiedRoomsIsDisplayTH = useSelector(selectOccupiedRoomsIsDisplayTH);
  const adrIsDisplayTH = useSelector(selectAdrIsDisplayTH);
  const avgBookingWindowIsDisplayTH = useSelector(selectAvgBookingWindowIsDisplayTH);
  const avgLosIsDisplayTH = useSelector(selectAvgLosIsDisplayTH);
  const cxlIsDisplayTH = useSelector(selectCxlIsDisplayTH);
  //
  const isPrimary = useSelector(selectIsPrimary);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  //
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const movingAverage = useSelector(selectMovingAverage);
  const localCurrency = useSelector(selectLocalCurrency);
  //
  const isTsRevenueRibbonView = useSelector(selectIsTsRevenueRibbonView);
  const isTsOccupiedRoomsRibbonView = useSelector(selectIsTsOccupiedRoomsRibbonView);
  const isTsAdrRibbonView = useSelector(selectIsTsAdrRibbonView);
  const breakdownData = useSelector(selectBreakdownDetails);
  // focus on option list of selected breakdown
  const focusOnList = useMemo(
    () =>
      getFocusOnFilter(
        breakdownData,
        getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed)
      ),
    [breakdownData, visualFilters, chartBreakdown, isDetailed]
  );

  // Triggers when filter values dependencies changes
  useEffect(() => {
    if (dateComparison?.length === 0 && !bookingDate && !targetDate && !hotelId) return;
    // Generate trend time filter url
    const trendTimeUrl = generateTrendTimeShareUrl(
      pmsSyncToggle,
      hotelId,
      targetDate,
      vOTB,
      bookingDate,
      tabularView,
      alignmentToggle,
      symmetricComparisonToggle,
      weeklyViewToggle,
      isCumulative,
      startDateComparison,
      endDateComparison,
      customDate,
      dateComparison,
      visualFilters,
      chartBreakdown,
      focusOn,
      hotelFocusOn,
      isDetailed,
      enableHotelGroups,
      hotelId,
      segmentGroup1,
      segmentGroup2,
      normalizedViewToggle,
      enableSecondaryHotelGroups,
      isTrendForecast,
      activeTab,
      revenueIsDisplayTT,
      occupiedRoomsIsDisplayTT,
      adrIsDisplayTT,
      avgBookingWindowIsDisplayTT,
      avgLosIsDisplayTT,
      cxlIsDisplayTT,
      revenueIsDisplayTS,
      occupiedRoomsIsDisplayTS,
      adrIsDisplayTS,
      avgBookingWindowIsDisplayTS,
      avgLosIsDisplayTS,
      cxlIsDisplayTS,
      revenueIsDisplayTH,
      occupiedRoomsIsDisplayTH,
      adrIsDisplayTH,
      avgBookingWindowIsDisplayTH,
      avgLosIsDisplayTH,
      cxlIsDisplayTH,
      compareWindowCollapseToggle,
      isPrimary,
      isPercentageChange,
      isTrendDeAggregate,
      isMovingAverage,
      movingAverage,
      localCurrency,
      isTsRevenueRibbonView,
      isTsOccupiedRoomsRibbonView,
      isTsAdrRibbonView,
      focusOnList
    );
    //
    dispatch(trendActions.setTrendTimeUrl(trendTimeUrl));
  }, [
    pmsSyncToggle,
    bookingDate,
    visualFilters,
    dateComparison,
    alignmentToggle,
    targetDate,
    hotelId,
    tabularView,
    symmetricComparisonToggle,
    weeklyViewToggle,
    isCumulative,
    vOTB,
    startDateComparison,
    endDateComparison,
    customDate,
    visualFilters,
    chartBreakdown,
    focusOn,
    isDetailed,
    enableHotelGroups,
    segmentGroup1,
    segmentGroup2,
    normalizedViewToggle,
    hotelFocusOn,
    enableSecondaryHotelGroups,
    isTrendForecast,
    activeTab,
    revenueIsDisplayTT,
    occupiedRoomsIsDisplayTT,
    adrIsDisplayTT,
    avgBookingWindowIsDisplayTT,
    avgLosIsDisplayTT,
    cxlIsDisplayTT,
    revenueIsDisplayTS,
    occupiedRoomsIsDisplayTS,
    adrIsDisplayTS,
    avgBookingWindowIsDisplayTS,
    avgLosIsDisplayTS,
    cxlIsDisplayTS,
    revenueIsDisplayTH,
    occupiedRoomsIsDisplayTH,
    adrIsDisplayTH,
    avgBookingWindowIsDisplayTH,
    avgLosIsDisplayTH,
    cxlIsDisplayTH,
    compareWindowCollapseToggle,
    isPrimary,
    isPercentageChange,
    isTrendDeAggregate,
    isMovingAverage,
    movingAverage,
    localCurrency,
    isTsRevenueRibbonView,
    isTsOccupiedRoomsRibbonView,
    isTsAdrRibbonView,
    focusOnList,
  ]);
};
export default useGenerateTrendUrl;
